/**
 * STY-578: word-level diffing for submission revisions.
 *
 * Deliberately dependency-free. A diff library would be the obvious choice, but
 * this app is on Node 8 / npm 5 whose installs are already flaky enough that the
 * Docker build has a documented retry step — adding a dependency to render a
 * read-only panel is not worth reintroducing that risk.
 */

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ldquo: '\u201c', rdquo: '\u201d', lsquo: '\u2018', rsquo: '\u2019',
  mdash: '\u2014', ndash: '\u2013', hellip: '\u2026'
}

function decodeEntity (whole, code) {
  if (code.charAt(0) === '#') {
    const num = code.charAt(1).toLowerCase() === 'x'
      ? parseInt(code.slice(2), 16)
      : parseInt(code.slice(1), 10)
    return isNaN(num) ? whole : String.fromCharCode(num)
  }

  const named = NAMED_ENTITIES[code.toLowerCase()]
  // Leave anything unrecognised alone rather than dropping it — silently eating
  // text would be worse than showing an entity.
  return named === undefined ? whole : named
}

/**
 * Convert stored field content to the prose a person actually wrote.
 *
 * These fields are edited through ReactQuill and persisted as its getHTML()
 * output, so the stored value is markup: paragraphs are wrapped in <p>, and
 * apostrophes and quotes arrive as entities. Diffing that directly compares
 * tags rather than words and renders <p> and &#39; on screen.
 *
 * This is not hypothetical. Of 2,230 blobs edited in the last 180 days in
 * production, 423 contain <p> tags and 151 contain entities. It was invisible
 * to every check I ran because the local fixtures were written as plain text.
 *
 * KNOWN LIMIT: an anchor collapses to its text, so a link whose href changed
 * but whose wording did not reads as unchanged. Three rows in that same sample
 * contain links, so this is not pressing — but it matters for the SEO backlink
 * work in phase 3, which will need a richer representation than plain text.
 */
export function htmlToText (value) {
  const raw = String(value == null ? '' : value)

  // Most fields are plain; skip the work and any risk of altering them.
  if (raw.indexOf('<') === -1 && raw.indexOf('&') === -1) return raw

  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, decodeEntity)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Split into words, each carrying its own trailing whitespace.
 *
 * Emitting whitespace as separate tokens looks equivalent and is not. Spaces
 * are identical on both sides, so the LCS matches them, and a replaced word
 * comes out as removed-word, added-word, shared-space — which renders as
 * "soA manyrelaxed prettycelebration": every pair of adjacent changes collides
 * and the shared space lands after the pair instead of between it. The diff is
 * correct and unreadable, which for a tool whose whole job is reading diffs is
 * the same as being wrong.
 *
 * Binding the space to the word it follows keeps replacements separated.
 */
export function tokenize (text) {
  const s = String(text == null ? '' : text)
  const words = s.match(/\S+\s*/g) || []
  const leading = s.match(/^\s+/)

  // Leading whitespace has no preceding word to attach to, so it stands alone.
  return leading ? [leading[0]].concat(words) : words
}

/**
 * Flatten the editorial blob into labelled prose blocks.
 *
 * Only text is included. Images, boards, and placement live elsewhere in the
 * blob and are not versioned (see the ticket) — showing them here would imply
 * a history we do not actually keep.
 */
export function flatten (data) {
  const blocks = []
  if (!data) return blocks

  // Blocks are keyed by label PLUS how many times that label has already been
  // seen. The editor allows duplicate and blank custom field names, and keying
  // on the label alone let a later duplicate overwrite an earlier one — so a
  // revision that changed only the first of two identically named fields was
  // reported as unchanged. Occurrence order survives unrelated fields being
  // added or removed, which raw indices would not.
  //
  // KNOWN LIMIT: if a duplicate is INSERTED BEFORE, or dragged ahead of, another
  // duplicate in the same section, the occurrence numbers shift and those two
  // blocks pair with the wrong prior revision — an existing field then reads as
  // edited rather than a new one as added.
  //
  // There is no better key available. Fields carry no identity in the stored
  // blob (CustomSection addField pushes {name, text}), and although custom
  // SECTIONS are stamped with generateId(), no production blob actually
  // contains one: across 4,666 blobs edited in the last year, zero carry an id
  // and zero have a blank field name. So the failure needs two identically
  // named fields in one section AND a reorder between revisions, on data shapes
  // that do not currently occur.
  //
  // The real fix is stamping ids on fields at write time, which changes what is
  // stored and belongs in its own ticket rather than riding along in a
  // read-only history panel.
  const seen = {}
  const add = (label, text) => {
    const n = seen[label] === undefined ? 0 : seen[label] + 1
    seen[label] = n
    blocks.push({ key: label + '#' + n, label: label, text: htmlToText(text) })
  }

  if (data.title) add('Title', data.title)
  if (data.subtitle) add('Subtitle', data.subtitle)
  // STY-580. First, because that is where it appears on the post, and because
  // a drafted opening is usually the only thing that changed in its revision:
  // burying it under the section fields would make the editor hunt for it.
  if (data.intro) add('Opening paragraph', data.intro)

  const sections = data.sections || []
  sections.forEach((section) => {
    const fields = (section && section.fields) || []
    fields.forEach((field) => {
      if (!field) return
      add([section.name, field.name].filter(Boolean).join(' › ') || 'Untitled field', field.text)
    })
  })

  return blocks
}

// Above this, the O(n*m) table gets big enough to block the UI thread for
// noticeably long. Editorial fields are nowhere near it — the largest blob in
// production is under 50KB in total — but a runaway value should degrade to a
// coarse answer rather than freeze the browser.
const MAX_TOKENS = 2500

/**
 * Word-level diff via longest common subsequence.
 *
 * Returns [{ value, added, removed }]. Unchanged runs carry neither flag.
 */
export function diffWords (before, after) {
  const a = tokenize(before)
  const b = tokenize(after)

  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    if (a.join('') === b.join('')) return [{ value: a.join(''), added: false, removed: false }]
    return [
      { value: a.join(''), added: false, removed: true },
      { value: b.join(''), added: true, removed: false }
    ]
  }

  // lengths[i][j] = LCS length of a[i..] and b[j..]
  const lengths = []
  for (let i = 0; i <= a.length; i++) lengths.push(new Array(b.length + 1).fill(0))

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lengths[i][j] = a[i] === b[j]
        ? lengths[i + 1][j + 1] + 1
        : Math.max(lengths[i + 1][j], lengths[i][j + 1])
    }
  }

  const out = []
  const push = (value, added, removed) => {
    const last = out[out.length - 1]
    // Merge adjacent runs of the same kind so the rendered output is a few
    // spans rather than one per word.
    if (last && last.added === added && last.removed === removed) last.value += value
    else out.push({ value: value, added: added, removed: removed })
  }

  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { push(a[i], false, false); i++; j++ }
    else if (lengths[i + 1][j] >= lengths[i][j + 1]) { push(a[i], false, true); i++ }
    else { push(b[j], true, false); j++ }
  }
  while (i < a.length) { push(a[i], false, true); i++ }
  while (j < b.length) { push(b[j], true, false); j++ }

  return out
}

/**
 * Split diff parts into the two columns of a side-by-side view.
 *
 * `before` is everything that existed in A: unchanged runs plus deletions.
 * `after` is everything that exists in B: unchanged runs plus insertions.
 * Each column therefore reads as continuous prose in its own right, which is
 * the entire point of the layout — inline interleaving makes both versions
 * unreadable exactly when the rewrite is heaviest, and heavy rewrites are what
 * our editors do.
 *
 * Same model WordPress uses: two columns, with word-level highlighting inside
 * each rather than colouring the whole cell.
 */
export function sides (parts) {
  return {
    before: (parts || []).filter(p => !p.added),
    after: (parts || []).filter(p => !p.removed)
  }
}

/**
 * Pair two revisions' blocks by label.
 *
 * Union rather than intersection, and order-preserving from whichever side has
 * it: a field the editor deleted outright is exactly the kind of change worth
 * seeing, and intersecting would hide it.
 */
export function pairBlocks (beforeData, afterData) {
  const before = flatten(beforeData)
  const after = flatten(afterData)

  const byKey = {}
  before.forEach((b) => { byKey[b.key] = { key: b.key, label: b.label, before: b.text, after: '' } })
  after.forEach((b) => {
    if (byKey[b.key]) byKey[b.key].after = b.text
    else byKey[b.key] = { key: b.key, label: b.label, before: '', after: b.text }
  })

  const order = []
  const seen = {}
  before.concat(after).forEach((b) => {
    if (!seen[b.key]) { seen[b.key] = true; order.push(b.key) }
  })

  return order.map((key) => {
    const pair = byKey[key]
    return {
      key: pair.key,
      label: pair.label,
      before: pair.before,
      after: pair.after,
      changed: String(pair.before || '') !== String(pair.after || ''),
      parts: diffWords(pair.before, pair.after)
    }
  })
}
